import { useState } from 'react';
import { formatHours, type ResourceLineItem } from '@forecastify/shared';
import { DataList, Dialog, type DataListRow } from '@forecastify/ui';
import { useT } from '../../i18n';
import { contractTypeLabel } from '../contracts/contract-type-label';
import { listLabels } from '../delivery/clients-screen';
import { useRowPages } from '../use-row-pages';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ EVERY LINE ITEM'S FIGURES AT ONCE — round 5 item 29.                 │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"A soronkénti info gomb mellé még kell egy globális info gomb is a szűrő mellé, amire
 * rákattintva feljön egy modal táblázat ami az összes lineitemre kiadja ezeket a statisztikákat egy
 * overview nézetben."*
 *
 * ⚠️⚠️ **THE SAME EIGHT FIGURES AS THE ROW'S POP-OVER**, by the same rules — `ResourceLineItem`
 * is `ResourceItem`'s figures taken over the whole deliverable rather than one person's booking,
 * one row per line item of the project, summed by the server (`resource.service.ts`, `projectSide`).
 *
 * ⚠️⚠️ 17 Oct 2026 · **THE KIT'S LIST, WITH A SEARCH AND THE SETTLED PAGER, AND ALWAYS IN DATE
 * ORDER** — the product owner. It was a hand-built table sorted by planned hours. Now `DataList`:
 * a search over the name, the project and the contract, `[<] [selector] [>] [total]`, and the rows
 * ordered by the line item's OWN start date (*Kezdés*, as the contract page shows it), undated ones
 * last. No column sorts, because the order is the point.
 */

const PAGE_SIZE = 20;

/** ⚠️ Hours, or an en dash where the figure does not apply — `line-item-summary.tsx`'s rule. */
function hours(value: string): string {
  return Number(value) === 0 ? '–' : formatHours(value);
}

/** Chronological: the line item's own start, then its end, then its name; undated ones last. */
function byDate(a: ResourceLineItem, b: ResourceLineItem): number {
  if (a.startsOn !== b.startsOn) {
    if (a.startsOn === null) return 1;
    if (b.startsOn === null) return -1;
    return a.startsOn.localeCompare(b.startsOn);
  }
  if (a.endsOn !== b.endsOn) {
    if (a.endsOn === null) return 1;
    if (b.endsOn === null) return -1;
    return a.endsOn.localeCompare(b.endsOn);
  }
  return a.lineItemName.localeCompare(b.lineItemName);
}

export function LineItemOverview({
  items,
  open,
  onClose,
}: {
  /** The project's line items — `ResourceView.projectLineItems`, every one, window or not. */
  readonly items: readonly ResourceLineItem[];
  readonly open: boolean;
  readonly onClose: () => void;
}) {
  const t = useT();
  const [query, setQuery] = useState('');

  const needle = query.trim().toLowerCase();
  const rows: DataListRow[] = [...items]
    .filter(
      (item) =>
        needle === '' ||
        [item.lineItemName, item.projectName ?? '', item.contractIdentifier ?? '']
          .join(' ')
          .toLowerCase()
          .includes(needle),
    )
    .sort(byDate)
    .map((item) => {
      const estimate = Number(item.estimateHours);
      const planned = Number(item.plannedHours);
      const ratio = estimate > 0 ? Math.round((planned / estimate) * 100) : null;
      return {
        id: item.lineItemId,
        cells: {
          name: {
            text: item.lineItemName,
            bold: true,
            /* ⚠️ The project and the contract's kind under the name — the row pop-over's header. */
            sub: [item.projectName, contractTypeLabel(t, item.contractType)]
              .filter((one) => one !== null && one !== '')
              .join(' · '),
            /* ⚠️ A closed one is listed — closing blocks nothing (U-17) — and said to be closed. */
            ...(item.closed
              ? { badge: { label: t('lineItem.closed'), variant: 'secondary' as const } }
              : {}),
          },
          people: { text: item.memberIds.length, mono: true },
          ratio:
            ratio === null
              ? { text: '–', muted: true }
              : {
                  text: `${String(ratio)}%`,
                  progress: {
                    value: Math.min(100, ratio),
                    label: t('resource.tip.ratio'),
                    ...(planned > estimate ? { color: 'var(--destructive)' } : {}),
                  },
                },
          startsOn: { text: item.startsOn ?? '–', mono: true, muted: item.startsOn === null },
          endsOn: { text: item.endsOn ?? '–', mono: true, muted: item.endsOn === null },
          estimate: { text: hours(item.estimateHours), mono: true },
          planned: { text: hours(item.plannedHours), mono: true, bold: true },
          overtime: {
            text: hours(item.overtimeHours),
            mono: true,
            muted: Number(item.overtimeHours) === 0,
          },
          tracked: { text: hours(item.trackedHours), mono: true },
          remaining: { text: hours(item.remainingHours), mono: true },
        },
      };
    });

  const paged = useRowPages(rows, query, PAGE_SIZE);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('resource.overview.title')}
      width="min(1100px, 96vw)"
      closeLabel={t('common.close')}
    >
      <div className="overflow-hidden rounded-lg border">
        <DataList
          columnsKey="line-item-overview"
          card={{
            title: 'name',
            defaults: { details: ['people', 'ratio', 'tracked', 'remaining'] },
          }}
          selectable={false}
          columns={[
            { key: 'name', label: t('effort.col.lineItem') },
            { key: 'people', label: t('resource.overview.people'), width: 84, align: 'right' },
            { key: 'ratio', label: t('resource.tip.ratio'), width: 124 },
            { key: 'startsOn', label: t('contract.column.startsOn'), width: 100 },
            { key: 'endsOn', label: t('contract.column.endsOn'), width: 100 },
            { key: 'estimate', label: t('contract.column.estimate'), width: 84, align: 'right' },
            { key: 'planned', label: t('resource.tip.plan'), width: 84, align: 'right' },
            { key: 'overtime', label: t('resource.tip.overtime'), width: 84, align: 'right' },
            { key: 'tracked', label: t('contract.column.tracked'), width: 84, align: 'right' },
            { key: 'remaining', label: t('resource.tip.remaining'), width: 84, align: 'right' },
          ]}
          rows={paged.rows}
          page={paged.page}
          search={query}
          onSearchChange={setQuery}
          empty={{ icon: 'list', title: t('resource.overview.empty'), body: '' }}
          labels={listLabels(t)}
        />
      </div>
    </Dialog>
  );
}
